/**
 * `/$org/settings/usage` — Usage & allowances. Every member has an included
 * amount of model usage per calendar month (the platform's standard unless
 * Amoreg set one for them); past it, usage is billed on demand at a higher
 * rate unless an organization admin caps the member.
 *
 * The page shows the month as a whole first — the organization's spend
 * against every allowance together, on one meter — and then each member's
 * allowance, edited in a small dialog: the on-demand switch is the admin's,
 * the amount is Amoreg's (operators only; the API refuses anyone else).
 *
 * @module components/settings/UsageAllowancesSettings
 */
import { UsageDay } from "@amoreg/contracts";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowUpRightIcon, SlidersHorizontalIcon } from "lucide-react";
import { useMemo, useState } from "react";

import type { OrgTeamMember } from "../../state/orgProfile";
import { useOrgs } from "../../state/orgs";
import { useUsage } from "../../state/usage";
import { usageCoverageComplete } from "../../usage/usageCoverage";
import { formatDayShort, formatGbp } from "../../usage/usageFormat";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { UsageCoverageNotice } from "../usage/UsageCoverageNotice";
import { MemberAllowanceDialog } from "./MemberAllowanceDialog";
import { MemberAvatar } from "./MemberAvatar";
import { MemberUsageCell } from "./MemberUsage";
import {
  allowanceMonth,
  memberDisplayName,
  sortMembers,
  totalIncludedGbp,
  type AllowanceMonth,
} from "./orgMembers.logic";
import { SettingsCard, SettingsPageHeader, SettingsStat, UsageMeter } from "./settingsCard";
import { SettingsPageContainer } from "./settingsLayout";
import { searchableSetting } from "./settingsSearch";
import { useOrgSettingsPage } from "./orgSettingsShared";
import { useOrgAllowances } from "./useOrgAllowances";

const NO_MEMBERS: ReadonlyArray<OrgTeamMember> = [];

export function UsageAllowancesSettingsPanel() {
  const { viewer } = useOrgs();
  const { org, profile, canManage, setMemberOnDemandUsage, setMemberModelAllowance, fallback } =
    useOrgSettingsPage("Usage & allowances");
  const team = profile?.team ?? NO_MEMBERS;
  const allowances = useOrgAllowances(org?.id ?? null, team, viewer.userId);
  const month = useMemo(() => allowanceMonth(), []);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const members = useMemo(() => sortMembers(team, viewer.userId), [team, viewer.userId]);

  if (fallback) return fallback;
  const isOperator = viewer.isGlobalAdmin;
  const canEdit = canManage || isOperator;
  const editing = team.find((member) => member.id === editingId) ?? null;
  const rate =
    allowances.overageMultiplier === null
      ? "a higher rate"
      : `${Number.parseFloat(allowances.overageMultiplier.toFixed(2))}× the standard rate`;

  return (
    <SettingsPageContainer className="gap-10">
      <SettingsPageHeader
        title="Usage & allowances"
        description={`Every member has an included amount of model usage each calendar month, set by Amoreg. Past it, usage is billed on demand at ${rate} — unless an admin caps the member at their allowance.`}
      />

      <OrgMonthCard
        organizationId={org!.id}
        organizationName={org!.name}
        team={team}
        month={month}
        standardGbp={allowances.standardGbp}
      />

      <SettingsCard
        id={searchableSetting("model-usage").id}
        title="Member allowances"
        description={
          canEdit
            ? "Choose a member to edit their limits. Capping a member pauses their conversations once the allowance is used."
            : "Only organization admins can cap a member at their allowance."
        }
      >
        {members.map((member) => (
          <AllowanceRow
            key={member.id}
            member={member}
            isViewer={member.id === viewer.userId}
            canEdit={canEdit}
            standardGbp={allowances.standardGbp}
            allowances={allowances}
            onEdit={() => {
              setEditingId(member.id);
              setEditOpen(true);
            }}
          />
        ))}
      </SettingsCard>

      {editing ? (
        <MemberAllowanceDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          member={editing}
          standardGbp={allowances.standardGbp}
          overageMultiplier={allowances.overageMultiplier}
          canManage={canManage}
          isOperator={isOperator}
          onToggleOnDemand={setMemberOnDemandUsage}
          onSetAllowance={setMemberModelAllowance}
        />
      ) : null}
    </SettingsPageContainer>
  );
}

function AllowanceRow({
  member,
  isViewer,
  canEdit,
  standardGbp,
  allowances,
  onEdit,
}: {
  readonly member: OrgTeamMember;
  readonly isViewer: boolean;
  readonly canEdit: boolean;
  readonly standardGbp: number | null;
  readonly allowances: ReturnType<typeof useOrgAllowances>;
  readonly onEdit: () => void;
}) {
  const name = memberDisplayName(member);
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_12rem_5.5rem] sm:px-5">
      <div className="flex min-w-0 items-center gap-3">
        <MemberAvatar member={member} />
        <div className="min-w-0">
          <p className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium text-foreground">{name}</span>
            {isViewer ? (
              <Badge variant="secondary" size="sm">
                You
              </Badge>
            ) : null}
          </p>
          {member.name ? (
            <p className="truncate text-xs text-muted-foreground">{member.email}</p>
          ) : null}
        </div>
      </div>
      <div className="col-start-1 row-start-2 sm:col-start-auto sm:row-start-auto">
        <MemberUsageCell
          member={member}
          standardGbp={standardGbp}
          spend={allowances.spendFor(member)}
        />
      </div>
      <div className="row-span-2 flex justify-end sm:row-span-1">
        {canEdit ? (
          <Button
            size="xs"
            variant="outline"
            aria-label={`Edit usage limits for ${name}`}
            onClick={onEdit}
          >
            <SlidersHorizontalIcon />
            Edit
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The month at a glance: the organization's spend against every member's
 * allowance together, the period, and how the allowances are made up.
 */
function OrgMonthCard({
  organizationId,
  organizationName,
  team,
  month,
  standardGbp,
}: {
  readonly organizationId: string;
  readonly organizationName: string;
  readonly team: ReadonlyArray<OrgTeamMember>;
  readonly month: AllowanceMonth;
  readonly standardGbp: number | null;
}) {
  const orgSegment = useParams({
    strict: false,
    select: (params) => (params as { org?: string }).org ?? "",
  });
  const usage = useUsage({
    sinceDay: UsageDay.make(month.sinceDay),
    untilDay: UsageDay.make(month.untilDay),
    timeZone: "UTC",
    organizationId,
    subject: "organization",
  });
  const included = totalIncludedGbp(team, standardGbp);
  const custom = team.filter((member) => member.modelAllowanceGbp !== null).length;
  const capped = team.filter((member) => !member.onDemandModelUsage).length;
  const spent = usage.merged.costGbp;
  // Answered at all — a quiet month is £0.00, not a missing figure.
  const hasSpend = usage.environments.some((environment) => environment.summary !== null);
  // The merged total leaves out environments that failed, run an older
  // contract or are still answering; only a full read is "all" of it.
  const complete = usageCoverageComplete({
    environments: usage.environments,
    staleEnvironments: usage.merged.staleEnvironments,
    coverageNotices: usage.coverageNotices,
    isPartial: usage.isPartial,
  });

  return (
    <SettingsCard
      title="This month"
      description={`${month.label} · allowances reset ${formatDayShort(month.resetsOn)}, in ${month.daysLeft} ${month.daysLeft === 1 ? "day" : "days"}`}
      action={
        orgSegment ? (
          <Button
            size="xs"
            variant="ghost-muted"
            render={<Link to="/$org/usage" params={{ org: orgSegment }} />}
          >
            Usage details
            <ArrowUpRightIcon />
          </Button>
        ) : null
      }
    >
      <div className="space-y-3 px-4 py-5 sm:px-5">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <div className="flex items-baseline gap-2">
            {hasSpend ? (
              <span className="text-2xl font-semibold tracking-[-0.02em] text-foreground tabular-nums">
                {formatGbp(spent)}
              </span>
            ) : usage.isPending ? (
              <Skeleton className="h-7 w-24" />
            ) : (
              <span className="text-2xl font-semibold text-muted-foreground">—</span>
            )}
            {included !== null ? (
              <span className="text-sm text-muted-foreground tabular-nums">
                of {formatGbp(included)} included
              </span>
            ) : null}
          </div>
          {hasSpend && complete && included !== null && included > 0 ? (
            <p className="text-xs text-muted-foreground tabular-nums">
              {Math.round((spent / included) * 100)}% used
            </p>
          ) : null}
        </div>
        {hasSpend && included !== null ? (
          <UsageMeter
            spentGbp={spent}
            includedGbp={included}
            label={`${organizationName} model usage this month`}
          />
        ) : (
          <div className="h-1.5 w-full rounded-full bg-foreground/[0.08]" />
        )}
        {complete || !hasSpend ? (
          <p className="text-xs text-muted-foreground/90">
            All model usage in {organizationName} this month, including automations. Each member is
            billed against their own allowance.
          </p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground/90" data-org-usage-partial="">
              Model usage counted so far in {organizationName} this month; the total is not complete.
              Each member is billed against their own allowance.
            </p>
            <UsageCoverageNotice
              environments={usage.environments}
              duplicateSources={[]}
              staleEnvironments={usage.merged.staleEnvironments}
              coverageNotices={usage.coverageNotices}
              isPartial={usage.isPartial}
            />
          </>
        )}
      </div>
      <div className="grid grid-cols-3 divide-x divide-border/60">
        <SettingsStat
          className="px-4 py-3.5 sm:px-5"
          label="Standard allowance"
          value={standardGbp === null ? "—" : formatGbp(standardGbp)}
          hint="per member, per month"
        />
        <SettingsStat
          className="px-4 py-3.5 sm:px-5"
          label="Custom allowances"
          value={custom}
          hint={custom === 1 ? "member set by Amoreg" : "members set by Amoreg"}
        />
        <SettingsStat
          className="px-4 py-3.5 sm:px-5"
          label="Capped"
          value={capped}
          hint={capped === 1 ? "member pauses at the allowance" : "members pause at the allowance"}
        />
      </div>
    </SettingsCard>
  );
}
